import React, { useEffect, useRef, useState, type JSX } from 'react';

import { useMutation } from '@apollo/client/react';
import log from 'loglevel';

import { useToast } from '@admin-platform/ui/components';
import X from '@admin-platform/ui/icons/solid/x-mark.icon.svg?react';

import { Button } from '../../../components/Button/Button';
import { Form } from '../../../components/Form/Form';
import { FormGroup } from '../../../components/Form/FormGroup';
import { Input } from '../../../components/Inputs/Input';
import { Label } from '../../../components/Inputs/Label';
import { SingleSelect } from '../../../components/Inputs/SingleSelect';
import LoadingIndicator from '../../../components/LoadingIndicator/LoadingIndicator';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../../../components/Modal/Modal';
import { logMutationResponse } from '../../../utils/logMutation';
import { parseGraphQLErrorForUser } from '../../../utils/normalizeGraphqlError';

import type { InviteUserToTenantMutationVariables } from './mutations/inviteUser.mutation.graphql';
import INVITE_USER_MUTATION from './mutations/inviteUser.mutation.graphql';

import styles from './InviteUserModal.module.css';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  email: string;
  name: string;
  role: string;
}

const DEFAULT_FORM_DATA: FormData = {
  email: '',
  name: '',
  role: 'member',
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

export function InviteUserModal({
  isOpen,
  onClose,
  onSuccess,
}: InviteUserModalProps): JSX.Element | null {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();

  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const resetForm = () => {
    setFormData(DEFAULT_FORM_DATA);
    setFormErrors({});
  };

  const [inviteUser, { loading: inviteLoading }] = useMutation<
    unknown,
    InviteUserToTenantMutationVariables
  >(INVITE_USER_MUTATION, {
    onCompleted: (result) => {
      logMutationResponse(result);
      toast('Invitation sent successfully!', 'success');
      onSuccess();
      resetForm();
    },
    onError: (error) => {
      log.error('Error inviting user:', error);
      const userFriendlyMessage = parseGraphQLErrorForUser(error);
      toast(userFriendlyMessage, 'error');
    },
  });

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Focus the first input when modal opens
  useEffect(() => {
    if (isOpen && emailInputRef.current) {
      const timer = setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await inviteUser({
      variables: {
        input: {
          email: formData.email.trim().toLowerCase(),
          name: formData.name.trim() || undefined,
          role: formData.role,
        },
      },
    });
  };

  const handleCancel = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalHeader>
        <ModalTitle>Invite Team Member</ModalTitle>
        <Button variant="outline" onClick={handleCancel} forIconOnly>
          <X className={styles.CloseIcon} />
        </Button>
      </ModalHeader>
      <ModalBody>
        <Form type="grid" onSubmit={handleSubmit} ref={formRef}>
          <FormGroup>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={formData.email}
              aria-invalid={!!formErrors.email}
              className={styles.Input}
              autoComplete="off"
              data-form-type="other"
              ref={emailInputRef}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  email: e.target.value,
                }));
                if (formErrors.email) {
                  setFormErrors((prev) => ({ ...prev, email: '' }));
                }
              }}
            />
            {formErrors.email && <p className={styles.Error}>{formErrors.email}</p>}
            <p className={styles.HelpText}>An invitation email will be sent to this address.</p>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="name">Name (Optional)</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              className={styles.Input}
              autoComplete="off"
              data-form-type="other"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="role">Role</Label>
            <SingleSelect
              id="role"
              options={ROLE_OPTIONS}
              value={formData.role}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  role: value as string,
                }))
              }
              placeholder="Select role"
            />
            <p className={styles.HelpText}>Admins have full access. Members have limited access.</p>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={handleCancel} disabled={inviteLoading}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={inviteLoading || !formData.email.trim()}
          onClick={() => formRef.current?.requestSubmit()}
        >
          {inviteLoading && <LoadingIndicator />}
          Send Invitation
        </Button>
      </ModalFooter>
    </Modal>
  );
}
